// Localização: frontend/src/hooks/data/useDocumentosPaciente.js (versão 0.1.0)
// Hook para gerenciar documentos do paciente

import { useState, useEffect, useCallback } from 'react';
import {
    listarDocumentosPaciente,
    uploadDocumentoPaciente,
    atualizarDocumentoPaciente,
    deletarDocumentoPaciente
} from '../../api/api';

/**
 * Hook para buscar e gerenciar documentos do paciente
 * @param {number} pacienteId - ID do paciente
 * @param {object} filtros - Filtros opcionais { categoria, entidade_origem, search, startDate, endDate, page, limit }
 * @returns {object} { documentos, paginacao, isLoading, error, refetch, uploadDocumento, atualizarDocumento, deletarDocumento }
 */
export function useDocumentosPaciente(pacienteId, filtros = {}) {
    const [documentos, setDocumentos] = useState([]);
    const [paginacao, setPaginacao] = useState({
        total: 0,
        page: 1,
        limit: 20,
        totalPages: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchDocumentos = useCallback(async () => {
        if (!pacienteId) {
            setDocumentos([]);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await listarDocumentosPaciente(pacienteId, filtros);
            const data = response?.data?.data || [];
            
            // ✅ CORREÇÃO: Garantir que paginacao sempre tenha todas as propriedades necessárias
            const paginacaoData = response?.data?.paginacao || {};
            const paginacaoCompleta = {
                total: paginacaoData.total ?? 0,
                page: paginacaoData.page ?? (filtros.page || 1),
                limit: paginacaoData.limit ?? (filtros.limit || 20),
                totalPages: paginacaoData.totalPages ?? 0
            };

            setDocumentos(data);
            setPaginacao(paginacaoCompleta);
        } catch (err) {
            console.error('Erro ao buscar documentos:', err);
            setError(err?.response?.data?.message || err?.message || 'Erro ao carregar documentos.');
            setDocumentos([]);
        } finally {
            setIsLoading(false);
        }
    }, [
        pacienteId, 
        filtros.categoria, 
        filtros.search, 
        filtros.startDate, 
        filtros.endDate, 
        filtros.page, 
        filtros.limit,
        filtros.entidade_origem
    ]);

    useEffect(() => {
        fetchDocumentos();
    }, [fetchDocumentos]);

    /**
     * Faz upload de um novo documento
     * @param {FormData} formData - FormData com arquivo e metadados
     * @returns {Promise<object>} - Documento criado
     */
    const uploadDocumento = useCallback(async (formData) => {
        try {
            const response = await uploadDocumentoPaciente(pacienteId, formData);
            // Recarregar lista após upload
            await fetchDocumentos();
            return response?.data?.data;
        } catch (err) {
            console.error('Erro ao fazer upload do documento:', err);
            throw err;
        }
    }, [pacienteId, fetchDocumentos]);

    /**
     * Atualiza metadados de um documento
     * @param {number} documentoId - ID do documento
     * @param {object} metadados - Metadados { titulo, descricao, categoria }
     * @returns {Promise<object>} - Documento atualizado
     */
    const atualizarDocumento = useCallback(async (documentoId, metadados) => {
        try {
            const response = await atualizarDocumentoPaciente(pacienteId, documentoId, metadados);
            // Recarregar lista após atualização
            await fetchDocumentos();
            return response?.data?.data;
        } catch (err) {
            console.error('Erro ao atualizar documento:', err);
            throw err;
        }
    }, [pacienteId, fetchDocumentos]);

    /**
     * Deleta um documento
     * @param {number} documentoId - ID do documento
     * @returns {Promise<void>}
     */
    const deletarDocumento = useCallback(async (documentoId) => {
        try {
            await deletarDocumentoPaciente(pacienteId, documentoId);
            // Recarregar lista após deleção
            await fetchDocumentos();
        } catch (err) {
            console.error('Erro ao deletar documento:', err);
            throw err;
        }
    }, [pacienteId, fetchDocumentos]);

    return {
        documentos,
        paginacao,
        isLoading,
        error,
        refetch: fetchDocumentos,
        uploadDocumento,
        atualizarDocumento,
        deletarDocumento
    };
}

export default useDocumentosPaciente;

